<template>
  <div class="base-list-container">
    <div class="controls">
      <dppl-auto-sized-button
        v-if="showModalButton"
        light
        class="new-item-button mt-4 px-10"
        @click="setModal({ field: 'settings', value: true })"
      >
        <slot name="settingsButtonContent" />
      </dppl-auto-sized-button>

      <slot
        name="settingsModal"
        v-bind="{
          show: show.settings,
          onClose: onModalClose,
          onCreate: createItem,
        }"
      />
    </div>

    <SettingsListSkeletonLoader v-if="pageLoading || listLoading" />

    <slot
      v-else
      name="list"
      :base-list="this"
    >
      <div
        v-for="(item, index) in visibleItems"
        :key="item.id"
        class="base-item d-flex align-center"
      >
        <v-progress-linear
          v-if="item.id === showItemProgressId"
          class="ma-4 progress-bar"
          color="brandPrimary"
          indeterminate
        />
        <NoEditorWrapper
          v-if="listType === 'router' && item.id !== showItemProgressId"
          class="w-100"
        >
          <RouterListItem
            :item="item"
            :menu-items="menuItems"
            :show-item-progress-id="showItemProgressId"
            @menu-click="handleEvent"
          >
            <template #itemBadge="{ item: badgeItem }">
              <slot
                name="itemBadge"
                v-bind="{ item: badgeItem }"
              />
            </template>
          </RouterListItem>
        </NoEditorWrapper>
        <ToggleListItem
          v-if="listType === 'toggle' && item.id !== showItemProgressId"
          :is-team-admin="isTeamAdmin"
          :integrated="integrated"
          :item="item"
          :menu-items="menuItems"
          :show-item-progress-id="showItemProgressId"
          :toggle-labels="toggleLabels"
          :toggle-actions="toggleActions"
          :list-field-names="listFieldNames"
          :item-disabled="itemDisabledIndex === index"
          @toggle-click="submit({ value: $event, index })"
          @menu-click="handleEvent"
        />
      </div>
      <Pagination
        v-if="listItems.length > itemsPerPage"
        v-model="paginationPage"
        :length="paginationLength"
      />

      <div
        v-if="showNewItemLoading"
        class="base-item shadow d-flex align-center"
      >
        <v-progress-linear
          class="ma-4 progress-bar"
          indeterminate
          color="brandPrimary"
        />
      </div>
    </slot>
    <DeleteModal
      :show="deleteModalVisible"
      :document-type="documentType"
      :name="user"
      @close="deleteModalVisible = false"
      @delete="deleteItem(jobId)"
    />
    <DuplicateModal
      :show="duplicateModalVisible"
      :document-type="documentType"
      :doc-title="jobToDuplicate ? jobToDuplicate.title : ''"
      @close="duplicateModalVisible = false"
      @duplicate="duplicateItem($event)"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import api from '@/api/api';
import { isEmpty } from 'lodash-es';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import DuplicateModal from '@/components/shared/DuplicateModal.vue';
import SettingsListSkeletonLoader from '@/components/shared/SkeletonLoaders/settings/SettingsListSkeletonLoader.vue';
import Pagination from '@/components/shared/Pagination.vue';
import { getQueryParamsFromURL } from '@/helpers/routing';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useModals } from '@/stores/modals/modals';
import { useSettingsConfig } from '@/stores/settings/config';
import { useAppPagination } from '@/stores/app/pagination';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppBase } from '@/stores/app/base';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import NoEditorWrapper from '@/components/editor/NoEditorWrapper.vue';
import RouterListItem from './RouterListItem.vue';
import ToggleListItem from './ToggleListItem.vue';

export default {
  name: 'BaseList',

  components: {
    Pagination,
    RouterListItem,
    ToggleListItem,
    SettingsListSkeletonLoader,
    DeleteModal,
    DuplicateModal,
    DpplAutoSizedButton,
    NoEditorWrapper,
  },

  props: {
    apiNames: { type: Object, default: () => ({}) },
    toggleLabels: { type: Object, default: () => ({ active: 'Yes', inactive: 'No' }) },
    toggleActions: { type: Object, default: () => ({}) },
    listFieldNames: { type: Object, default: () => ({}) },
    menuItems: { type: Array, default: () => [] },
    metaName: { type: String, default: '' },
    apiItemsField: { type: String, default: '' },
    listType: { type: String, default: '' },
    documentType: { type: String, default: '' },
    contentFieldAlias: { type: String, default: '' },
    listItemAlias: { type: String, default: 'name' },
    showModalButton: { type: Boolean, default: true },
    showNewModal: { type: Boolean, default: false },
    jobIdToReturnOnCancel: { type: String, default: '' },
    path: { type: String, default: '' },
    showBaseListLoader: { type: Boolean, default: false },
    itemsPerPage: { type: Number, default: 20 },
  },

  data() {
    return {
      listItems: [],
      deleteModalVisible: false,
      duplicateModalVisible: false,
      jobId: null,
      user: '',
      showNewItemProgress: this.showBaseListLoader, // show progress bar on a new list item
      showItemProgressId: null, // show progress bar on an existing list item
      itemDisabledIndex: false,
      jobToDuplicate: null,
    };
  },

  computed: {
    ...mapState(useAppLoading, ['pageLoading', 'listLoading']),
    ...mapState(useSettingsConfig, ['config']),
    ...mapState(useAppPagination, ['page']),
    ...mapState(useAppAuthData, ['integrated']),
    ...mapState(useModals, ['show']),
    ...mapState(useAppBase, ['browserBackOrForwardNavigation']),

    isTeamAdmin() {
      return !(this.documentType === 'team' && !this.config.canTeamAdmin);
    },
    paginationPage: {
      get() {
        return this.page;
      },
      set(newPage) {
        this.handlePageUpdate(newPage);
      },
    },
    paginationLength() {
      return Math.ceil(this.listItems.length / this.itemsPerPage);
    },
    visibleItems() {
      const { page, itemsPerPage } = this;
      return this.listItems.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    },
    showNewItemLoading() {
      return this.showNewItemProgress || this.showBaseListLoader;
    },
  },
  watch: {
    browserBackOrForwardNavigation: {
      immediate: true,
      handler(newValue) {
        if (newValue && window.location.pathname === this.path) {
          const searchParams = getQueryParamsFromURL();

          if (this.paginationPage !== searchParams?.page) this.handlePageUpdate(searchParams.page);
        }
      },
    },
  },
  async mounted() {
    if (isEmpty(this.apiNames)) { return; }

    if (this.showNewModal) {
      this.setModal({ field: 'settings', value: true });
    }

    await this.loadItems();

    const totalPages = Math.ceil(this.listItems.length / this.itemsPerPage);
    let page = Number(this.$route.query?.page) || 1;
    const changeQuery = !this.$route.query?.page || page > totalPages;

    if (page > totalPages) page = totalPages;

    this.updatePagination({ page, totalPages });

    if (changeQuery) {
      this.$router.replace({ query: { page } }).catch(() => false);
    }
  },
  beforeUnmount() {
    this.updatePagination({ page: 1, totalPages: 1 });
  },
  methods: {
    ...mapActions(useAppSnackbar, ['addSuccessSnackbar', 'addErrorSnackbar']),
    ...mapActions(useAppLoading, ['setLoading']),
    ...mapActions(useAppPagination, ['setPageNumber', 'updatePagination']),
    ...mapActions(useModals, ['setModal']),
    handlePageUpdate(page) {
      this.setPageNumber(page);

      this.$router.push({ query: { page } }).catch(() => false);
    },

    async loadItems() {
      const { [this.apiItemsField]: items } = await this.makeApiRequest({
        apiName: this.$props.apiNames.load,
        actionError: 'Could not load data',
      });

      this.listItems = items.map(({
        id,
        [this.listItemAlias]: name,
        is_default: isDefault,
        ...rest
      }) => ({
        id, name, default: isDefault, ...rest,
      }));
    },

    async makeApiRequest({ apiName, data, actionError }) {
      let response = null;
      if (apiName === this.$props.apiNames.load) {
        /* only show skeleton loaders when loading list */
        this.setLoading({ listLoading: true });
      }

      try {
        response = await api[apiName](data);
      } catch (e) {
        this.addErrorSnackbar({
          text: `Error: ${actionError} - ${e.message}`,
          timeout: 3500,
        });
        this.showNewItemProgress = false;
      } finally {
        this.setLoading({ listLoading: false });
      }
      return response || {};
    },

    async submit({ value, index }) {
      this.itemDisabledIndex = index;
      const { apiName, data } = value;
      await this.makeApiRequest({
        apiName: this.$props.apiNames[apiName],
        data,
        actionError: 'Setting could not be updated',
      });
      this.itemDisabledIndex = false;
    },

    showDeleteModal(jobId) {
      // pass username or document title to delete modal
      const { name } = this.listItems.find((user) => user.id === jobId);
      this.user = name;
      this.jobId = jobId;
      this.deleteModalVisible = true;
    },

    showDuplicateModal(jobId, title) {
      this.jobToDuplicate = { jobId, title };
      this.duplicateModalVisible = true;
    },

    handleEvent(methodName, id, name) {
      this[methodName].call(this, id, name);
    },

    async createItem(data) {
      this.setModal({ field: 'settings', value: false });
      this.showNewItemProgress = true;
      const { success, jobId: id } = await this.makeApiRequest({
        apiName: this.$props.apiNames.create,
        data,
        actionError: 'Could not create item',
      });

      if (success) {
        await this.$router.push({ name: 'editor-view', params: { id } });
        this.showNewItemProgress = false;
      }
    },

    async deleteItem(jobId) {
      this.deleteModalVisible = false;
      this.showItemProgressId = jobId;
      const { success } = await this.makeApiRequest({
        apiName: this.$props.apiNames.delete,
        data: { jobId },
        actionError: 'Could not delete item',
      });
      this.showItemProgressId = null;

      if (success) {
        const deleted = this.listItems.find((item) => item.id === jobId);
        this.listItems = this.listItems.filter((item) => item.id !== jobId);
        this.addSuccessSnackbar({ text: `${deleted.name} has been removed` });
      }
    },

    async duplicateItem({ title }) {
      if (this.duplicateModalVisible) {
        this.duplicateModalVisible = false;
      }
      this.showNewItemProgress = true;
      const { success, id, title: name } = await this.makeApiRequest({
        apiName: this.$props.apiNames.duplicate,
        data: { jobId: this.jobToDuplicate.jobId, title, from: 'library-menu' },
        actionError: 'Could not duplicate item',
      });

      if (success) {
        await this.$router.push({ name: 'editor-view', params: { id } });
        this.showNewItemProgress = false;
        this.listItems.push({ id, name, default: false });
        this.addSuccessSnackbar({ text: `${title} has been duplicated successfully` });
      }
    },

    async makeDefault(jobId) {
      this.showItemProgressId = jobId;
      const { success } = await this.makeApiRequest({
        apiName: this.$props.apiNames.makeDefault,
        data: { jobId },
        actionError: 'Could not set a new default',
      });
      this.showItemProgressId = null;

      if (success) {
        await this.loadItems();
        this.setPageNumber(1);
        this.$router.replace({ query: { page: undefined } }).catch(() => false);

        const isDefault = this.listItems.find((item) => item.default);
        this.addSuccessSnackbar({ text: `${isDefault.name} has been set to the new default` });
      }
    },

    async toggleIsHidden(item) {
      this.showItemProgressId = item.id;

      // eslint-disable-next-line no-param-reassign
      item.is_hidden = !item.is_hidden;

      const { success } = await this.makeApiRequest({
        apiName: this.$props.apiNames.makeHidden,
        data: { jobId: item.id, isHidden: item.is_hidden },
        actionError: 'Could not change template status',
      });

      this.showItemProgressId = null;

      if (!success) {
        await this.loadItems();
      }
    },

    onModalClose() {
      this.setModal({ field: 'settings', value: false });

      const { jobIdToReturnOnCancel: id } = this;
      if (id) {
        this.$router.replace({ name: 'editor-view', params: { id } });
      }
    },
  },
};
</script>

<style scoped lang="less">
@import '@/less/mixins';
.v-btn {
  text-transform:none !important;
}
.v-btn::before {
  background-color: @indigo-100;
}
.base-list-container {
  width: 100%;
}
.base-item {
  width: 100%;
  height: 68px;
  background-color: white;
  margin-bottom: 2px;
}
.controls {
  position: relative;
  margin-bottom: 24px;
  background-color: transparent;
}
.new-item-button :deep(.v-btn__content) {
  font-weight: 400;
}
.new-item-button {
  &.v-btn {
    background-color: @white !important;
    .shadow();

    &:hover {
      background-color: @text-brand !important;
    }
  }
}

.new-item-button:hover :deep(.v-btn__content) {
  color: white;
}

.progress-bar {
  transform: translateX(0) !important;
  left: auto !important;
}
</style>
